import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import classNames from 'classnames'
import type {SyntheticEvent} from 'react'
import {memo, useCallback} from 'react'

import {getBuild, getBuildType} from '../../../../../../../../selectors'
import type {BuildId, TestOccurrenceId} from '../../../../../../../../types'
import {formatDurationWithMilliseconds} from '../../../../../../../../utils/dateTimeUtils'
import {noop} from '../../../../../../../../utils/empty'
import TestInvestigations from '../../../../../../../common/Investigations/TestInvestigations'
import TestMutes from '../../../../../../../common/Mutes/TestMutes'
import BuildTestActions from '../../../../../../../packages/Tests/BuildTestActions/BuildTestActions'
import CopyTestNameIconButton from '../../../../../../../packages/Tests/CopyTestNameIconButton/CopyTestNameIconButton'
import TestFlakyLabel from '../../../../../../../packages/Tests/TestFlakyLabel/TestFlakyLabel'
import TestHistoryChartPopup from '../../../../../../../packages/Tests/TestHistoryChart/TestHistoryChartPopup'
import TestMetadataIcon from '../../../../../../../packages/Tests/TestMetadataIcon/TestMetadataIcon'
import {
  getBaseTestOccurrence,
  getTestOccurrenceMetadataCount,
  getTestOccurrenceRunOrder,
  getTestOccurrence,
} from '../../../../../../../packages/Tests/Tests.selectors'
import {getTestOccurrenceElementId} from '../../../../../../../packages/Tests/Tests.utils'
import TestStatus from '../../../../../../../packages/Tests/TestStatus/TestStatus'
import BuildTestRunBuildPath from '../TestRunBuildPath/TestRunBuildPath'

import BuildTestName from './TestName/TestName'

import styles from './TestItemAdvanced.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  buildId: BuildId
  testOccurrenceId?: TestOccurrenceId
  expanded: boolean
  checked: boolean
  invocation?: boolean
  withCheckbox?: boolean
  onToggleSelect: (id: TestOccurrenceId) => unknown
}

function TestItemAdvanced({
  className,
  buildId,
  testOccurrenceId,
  checked,
  expanded,
  invocation,
  withCheckbox,
  onToggleSelect = noop,
}: Props) {
  const {
    test,
    duration,
    build: testBuild,
  } = useAppSelector(state =>
    invocation
      ? getBaseTestOccurrence(state, testOccurrenceId)
      : getTestOccurrence(state, testOccurrenceId),
  ) ?? {}
  const {composite} = useAppSelector(state => getBuild(state, buildId)) ?? {}
  const testId = test?.id
  const testBuildTypeId = testBuild?.buildTypeId
  const {projectId: testProjectId} =
    useAppSelector(state => getBuildType(state, testBuildTypeId)) ?? {}
  const metadataCount =
    useAppSelector(state => getTestOccurrenceMetadataCount(state, testOccurrenceId)) ?? 0
  const runOrder = useAppSelector(state => getTestOccurrenceRunOrder(state, testOccurrenceId))
  const toggleSelect = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation()
      testOccurrenceId != null && onToggleSelect(testOccurrenceId)
    },
    [onToggleSelect, testOccurrenceId],
  )
  const formatedDuration = formatDurationWithMilliseconds(duration ?? 0)
  return (
    <div className={classNames(className, styles.row, {[styles.invocation]: invocation})}>
      <div className={styles.statusColumn} data-test-build-test-item={'status-text'}>
        {withCheckbox && (
          <div className={styles.checkbox}>
            <Checkbox
              aria-labelledby={getTestOccurrenceElementId(testOccurrenceId)}
              checked={checked}
              onClick={toggleSelect}
              onChange={toggleSelect}
              data-test-build-test-item={'checkbox-input'}
            />
          </div>
        )}
        <TestStatus testOccurrenceId={testOccurrenceId} withCounters={!invocation} />
      </div>
      <div className={styles.nameColumn}>
        {!invocation && (
          <>
            <TestMutes
              className={styles.testMutes}
              iconClassName={styles.testMutesIcon}
              buildId={buildId}
              testOccurrenceId={testOccurrenceId}
            />
            <TestInvestigations
              className={styles.testInvestigations}
              iconClassName={styles.testInvestigationsIcon}
              buildId={buildId}
              testOccurrenceId={testOccurrenceId}
              isShowOnlyIcon
            />
          </>
        )}
        <span data-test-build-test-item={'name'}>
          {invocation ? (
            <BuildTestRunBuildPath testOccurrenceId={testOccurrenceId} />
          ) : (
            <BuildTestName
              testOccurrenceId={testOccurrenceId}
              withTrancateName={!expanded}
              withPartsLink
            />
          )}
        </span>
        {!invocation && (
          <TestFlakyLabel className={styles.flakyLabel} buildId={buildId} testId={testId} />
        )}
        <TestMetadataIcon
          className={styles.testMetadataIcon}
          testOccurrenceId={testOccurrenceId}
          metadataCount={metadataCount}
        />
        {!invocation && (
          <CopyTestNameIconButton
            iconClassName={styles.copyTestNameIcon}
            testOccurrenceId={testOccurrenceId}
          />
        )}
      </div>
      <div className={styles.durationColumn} data-test-build-test-item={'duration'}>
        {testId != null && testProjectId != null && composite !== true ? (
          <TestHistoryChartPopup
            testId={testId}
            projectId={testProjectId}
            buildTypeId={testBuildTypeId}
            anchor={formatedDuration}
          />
        ) : (
          formatedDuration
        )}
      </div>
      <div className={styles.runOrderColumn} data-test-build-test-item={'order'}>
        {runOrder ?? 'Unknown'}
      </div>
      <div className={styles.actionsColumn} data-test-build-test-item={'actions'}>
        {!invocation && (
          <BuildTestActions
            className={styles.actions}
            anchorClassName={styles.actionsAnchor}
            buildId={buildId}
            testOccurrenceId={testOccurrenceId}
          />
        )}
      </div>
    </div>
  )
}

export default memo<Props>(TestItemAdvanced)
